import React from "react";
import { CompanyListModel } from "src/modules/company/types/Company";
import { UserProfilePermissionsModel } from "src/modules/profile/types/UserProfile";
import { Storage } from "src/utils/storage";

export interface UserState {
    isAuthenticated: boolean;
    email: string | null;
    company: CompanyListModel | null;
    roles: UserProfilePermissionsModel | null;
}

const initialState = {
    isAuthenticated: !!Storage.getItem("accessToken"),
    email: Storage.getItem("email"),
    company: Storage.getItem("currentCompany") || null,
    roles: Storage.getItem("userRoles") || null,
};

const UserStateContext = React.createContext(initialState) as any;
const UserDispatchContext = React.createContext(initialState) as any;

export const UserActionTypes = {
    SET: "set",
    CLEAR: "clear",
    SET_COMPANY: "setCompany",
    SET_ROLES: "setRoles",
    CLEAR_COMPANY_ROLES: "clearCompanyRoles",
};

const userReducer = (state: UserState, action: { type: string; payload: UserState }) => {
    switch (action.type) {
        case UserActionTypes.SET: {
            return action.payload;
        }
        case UserActionTypes.SET_COMPANY: {
            return { ...state, company: action.payload };
        }
        case UserActionTypes.CLEAR_COMPANY_ROLES: {
            return { ...state, company: null, roles: null };
        }
        case UserActionTypes.SET_ROLES: {
            return { ...state, roles: { ...action.payload } };
        }
        case UserActionTypes.CLEAR: {
            return {
                email: null,
                isAuthenticated: false,
                company: null,
                roles: null,
            };
        }
        default: {
            throw new Error(`Unhandled action type: ${action.type}`);
        }
    }
};

const UserProvider = ({ children }: any) => {
    const [state, dispatch] = React.useReducer(userReducer as any, initialState);

    return (
        <UserStateContext.Provider value={state}>
            <UserDispatchContext.Provider value={dispatch}>{children}</UserDispatchContext.Provider>
        </UserStateContext.Provider>
    );
};

const useUserState = () => {
    const user = React.useContext(UserStateContext);

    if (user === undefined) {
        throw new Error("useUserState must be used within a UserProvider");
    }

    return user;
};

const useUserDispatch: any = () => {
    const dispatch = React.useContext(UserDispatchContext);

    if (dispatch === undefined) {
        throw new Error("useUserDispatch must be used within a UserProvider");
    }

    return dispatch;
};

export { UserProvider, useUserState, useUserDispatch };
